<template>
  <div v-if="!showMobileFullscreen" class="v-select">
    <v-input
      ref="v-select-input"
      label="name"
      :value= "localValue"
      @input="url ? onInputSearch($event) : onInput($event)"
      :placeholder="placeholder"
      @focus="onFocus"
      @blur="onBlur"
      :small="small"
      v-bind="$attrs"
    />
    <div v-show="url ? (localOptions && focused) : focused" class="v-select__slot">
      <slot>
        <template v-if="localOptions">
          <vLocalOptions
            ref="localOptionsDesktop"
            :options="localOptions"
            :optionId="optionId"
            :label="label"
            :metaLabel="metaLabel"
            :groupLabel="groupLabel"
            :groupValues="groupValues"
            @setValue="setValue($event)"
          />
        </template>
      </slot>
    </div>
    <v-icon
      v-if="!url || url && localValue"
      :name="clearButton && localValue ? 'x' : 'caret'" 
      width="10"
      @click.native="clearButton && localValue ? resetInput() : ''"
      class="v-select__icon"
      :class="clearButton && localValue && 'v-select__icon--clear'"
    />
    <div v-if="fetching" class="v-select__spinner">
      <v-loading :small="small" />
    </div>
  </div>
  <div v-else>
    <vSelectMobile
      @closeFullscreen="showMobileFullscreen = false"
      @input="url ? onInputSearch($event) : onInput($event)"
      @resetInput="resetInput()"
      :active="activeOptions"
      :fetching="fetching"
    >
      <slot>
        <template v-if="localOptions">
          <vLocalOptions
            ref="localOptionsMobile"
            :options="localOptions"
            :optionId="optionId"
            :label="label"
            :metaLabel="metaLabel"
            :groupLabel="groupLabel"
            :groupValues="groupValues"
            @setValue="setValue($event)"
          />
        </template>
      </slot>
    </vSelectMobile>
  </div>
</template>

<script>
import vSelectMobile from "./v-select-mobile.vue";
import vLocalOptions from "./v-local-options.vue";

import axios from "axios";
const { CancelToken, isCancel } = axios;
import _ from "lodash";

export default {
  components: { vSelectMobile, vLocalOptions },
  props: {
    placeholder: {
      type: String,
      default: "Vælg én"
    },
    value: {
      type: String,
      default: ""
    },
    clearButton: {
      type: Boolean,
      default: true
    },
    options: {
      type: Array,
      default: null
    },
    optionId: {
      type: String,
      default: null
    },
    metaLabel: {
      type: String,
      default: null
    },
    groupLabel: {
      type: String,
      default: null
    },
    groupValues: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    url: {
      type: String,
      default: null
    },
    emitNewOptions: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    minChars: {
      type: Number,
      default: 2
    }
  },
  data() {
    return {
      optionsReady: false,
      focused: false,
      localValue: this.value,
      localOptions: this.options,
      fetching: false,
      showMobileFullscreen: false,
      activeOptions: null
    };
  },
  created() {
    if (!this.localOptions) {
      this.$on("setValue", data => {
        this.setValue(data);
      });
    }
  },
  beforeDestroy() {
    this.$off("setValue");
  },
  watch: {
    options(value) {
      this.localOptions = value;
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    }
  },
  methods: {
    setValue(value) {
      if (!value) {
        this.activeOptions = null;
      }
      // set child input with same value as active
      const children = !this.localOptions
        ? this.$children
        : this.isMobile
          ? this.$refs.localOptionsMobile.$children
          : this.$refs.localOptionsDesktop.$children;
      children.forEach(child => {
        if (child.$options.name !== "v-option") {
          return;
        }
        if (
          (this.optionId && !this.groupLabel) || this.metaLabel
            ? child.$el.children[0].innerText.toLowerCase() ===
              value.toLowerCase()
            : child.$el.innerText.toLowerCase() === value.toLowerCase()
        ) {
          child.$el.classList.add("v-select__option--active");
          this.activeOptions = child.$el.innerText;
        } else {
          child.$el.classList.remove("v-select__option--active");
        }
      });
      if (this.isMobile && value) {
        this.showMobileFullscreen = false;
      } else if (!this.isMobile) {
        this.$refs["v-select-input"].onReset();
      }
      this.localValue = value;
      this.$emit("input", { target: { value: this.localValue } });
      this.resetElements();
      if (value) {
        this.focused = false;
      }
    },
    onFocus() {
      if (this.isMobile) {
        // open mobile layout
        this.showMobileFullscreen = true;
      }
      this.focused = true;
    },
    onBlur(event) {
      // check if focus is lost to outside click
      if (
        !event.relatedTarget ||
        !event.relatedTarget.classList.contains("v-select__option")
      ) {
        this.focused = false;
      }
      // wait for click event to fire & clear localValue if no option is selected
      if (!this.localValue && !event.relatedTarget) {
        if (this.url) {
          this.localOptions = null;
        }
        if (!this.isMobile) {
          this.onInput({ target: { value: "" } });
          this.setValue("");
          this.focused = false;
        }
      }
    },
    onInput(event) {
      let groupLabelShown = [];
      if (event.target.value === "") {
        if (this.url) {
          this.localOptions = null;
        }
        this.setValue("");
      }
      // hide children elements where value !== search query
      const children = !this.localOptions
        ? this.$children
        : this.isMobile
          ? this.$refs.localOptionsMobile.$children
          : this.$refs.localOptionsDesktop.$children;
      children.forEach(child => {
        if (child.$options.name !== "v-option") {
          return;
        }
        if (
          !child.$el.innerText
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) &&
          event.target.value !== ""
        ) {
          child.$el.style.display = "none";
          if (groupLabelShown.includes(child.$el.parentElement.children[0])) {
            return;
          }
          child.$el.parentElement.children[0].classList.add(
            "v-select__group-label--hidden"
          );
        } else {
          child.$el.style.display = "block";
          groupLabelShown.push(child.$el.parentElement.children[0]);
          child.$el.parentElement.children[0].classList.remove(
            "v-select__group-label--hidden"
          );
        }
      });
    },
    onInputSearch(event) {
      if (event.target.value === "") {
        this.setValue("");
      }
      if (event.target.value.length < this.minChars) {
        return;
      }
      this.debouncedSearch(event.target.value);
    },
    stopLoading() {
      this.fetching = false;
    },
    debouncedSearch: _.debounce(function(value) {
      //replace {query} with search query
      const queryUrl = this.url.replace("{query}", value);
      this.fetch(queryUrl);
    }, 500),
    fetch(url) {
      this.fetching = true;

      // Cancel previous requests
      if (this._token) {
        this._token.cancel();
        this._token = null;
      }

      // Prepare cancel token
      this._token = CancelToken.source();

      // Perform request
      axios
        .get(url, { cancelToken: this._token.token })
        .then(response => {
          this.localOptions = response.data;
          this.debouncedEvent(response.data.length);
          this.stopLoading();
          if (this.emitNewOptions) this.$emit("new-options", response.data);
        })
        .catch(thrown => {
          if (!isCancel(thrown)) {
            this.stopLoading();
            this.$emit("fail");
          }
        });
    },
    resetInput() {
      if (this.url) {
        this.localOptions = null;
      } else if (!this.isMobile) {
        this.$refs["v-select-input"].onReset();
      }
      this.localValue = "";
      this.$emit("input", "");
      this.setValue("");
    },
    resetElements() {
      const children = !this.localOptions
        ? this.$children
        : this.isMobile
          ? this.$refs.localOptionsMobile.$children
          : this.$refs.localOptionsDesktop.$children;
      children.forEach(child => {
        if (child.$options.name !== "v-option") {
          return;
        }
        child.$el.style.display = "block";
        child.$el.parentElement.children[0].classList.remove(
          "v-select__group-label--hidden"
        );
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-select {
  position: relative;
  &__slot {
    position: absolute;
    box-shadow: 0 4px 10px 5px #e5e5e5;
    width: 100%;
    border-radius: 5px;
    z-index: 51;
    background-color: #fff;
    margin-top: 5px;
  }

  &__slot {
    max-height: 300px;
    overflow-y: auto;
  }

  &__icon {
    position: absolute;
    right: 1.2rem;
    top: 1.3rem;
    cursor: pointer;
    color: theme-color("placeholder");
    z-index: 50;
    color: theme-color("dark");
    pointer-events: none;

    &--clear {
      pointer-events: all;
    }

    &:hover {
      color: theme-color("gray-dark");
    }
  }

  &__group-label {
    color: #a6a6a6 !important;
    cursor: text;
    pointer-events: none;
    max-height: 50px;
    font-size: 0.9em;
    padding: 1rem;

    &--hidden {
      display: none;
    }
  }

  &__meta-label {
    float: inline-end;
    color: theme-color("placeholder");
  }

  & .v-input--small ~ &__icon {
    top: 0.8rem;
  }

  & .v-input--has-helper + .v-select__slot {
    top: 52px;
  }
  &__spinner {
    position: absolute;
    right: 1rem;
    top: 0.65rem;
    z-index: 51;
  }
}
</style>
